import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BACKEND_URL } from "../../config";

export const statsApi = createApi({
  reducerPath: "statsApi",
  keepUnusedDataFor: 300, // Keep cached data for 5 minutes
  tagTypes: ["StatsApi", "CustomerData"],
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getCompanySales: builder.query({
      query: ({ startDate, endDate }) => ({
        url: `/stats/portal/get-company-sales-stats`,
        method: "POST",
        body: {
          startDate: startDate
            ? new Date(startDate).toISOString().split("T")[0]
            : undefined,
          endDate: endDate
            ? new Date(endDate).toISOString().split("T")[0]
            : undefined,
        },
      }),
      providesTags: ["StatsApi"],
    }),
    getCustomers: builder.query({
      query: ({ startDate, endDate }) => ({
        url: "/sales/portal/search-all-customers",
        method: "POST",
        body: {
          startDate: startDate
            ? new Date(startDate).toISOString().split("T")[0]
            : undefined,
          endDate: endDate
            ? new Date(endDate).toISOString().split("T")[0]
            : undefined,
        },
      }),
      providesTags: ["CustomerData"],
    }),
    getTeamsStats: builder.query({
      query: ({ team, startDate, endDate }) => ({
        url: `/stats/portal/get-team-stats`,
        method: "POST",
        body: {
          team,
          startDate: startDate
            ? new Date(startDate).toISOString().split("T")[0]
            : undefined,
          endDate: endDate
            ? new Date(endDate).toISOString().split("T")[0]
            : undefined,
        },
      }),
      providesTags: ["StatsApi"],
    }),
    getTeamLeaderStats: builder.query({
      query: ({ startDate, endDate }) => ({
        url: `/stats/portal/get-team-leader-stats`,
        method: "POST",
        body: {
          startDate: startDate
            ? new Date(startDate).toISOString().split("T")[0]
            : undefined,
          endDate: endDate
            ? new Date(endDate).toISOString().split("T")[0]
            : undefined,
        },
      }),
      providesTags: ["StatsApi"],
    }),
    getTeamLeaderStatsSummary: builder.query({
      query: ({ startDate, endDate }) => ({
        url: `/stats/portal/get-team-leader-stats-summary`,
        method: "POST",
        body: {
          startDate: startDate
            ? new Date(startDate).toISOString().split("T")[0]
            : undefined,
          endDate: endDate
            ? new Date(endDate).toISOString().split("T")[0]
            : undefined,
        },
      }),
      providesTags: ["StatsApi"],
    }),

    getTeamLeaderStatsDetails: builder.query({
      query: ({ startDate, endDate }) => ({
        url: `/stats/portal/get-team-leader-stats-details`,
        method: "POST",
        body: {
          startDate: startDate
            ? new Date(startDate).toISOString().split("T")[0]
            : undefined,
          endDate: endDate
            ? new Date(endDate).toISOString().split("T")[0]
            : undefined,
        },
      }),
      providesTags: ["StatsApi"],
    }),

    getBoardOfGlory: builder.query({
      query: () => ({
        url: `/sales/portal/get-glory-rep`,
        method: "GET",
      }),
      providesTags: ["StatsApi"],
    }),
    getPersonalBest: builder.query({
      query: ({ name, year }) => ({
        url: `/stats/portal/get-personal-best`,
        method: "POST",
        body: {
          name,
          year,
        },
      }),
      providesTags: ["StatsApi"],
    }),
    getSalesRep: builder.query({
      query: ({ name, year }) => ({
        url: `/stats/portal/get-sales-rep`,
        method: "POST",
        body: {
          name,
          year,
        },
      }),
      providesTags: ["StatsApi"],
    }),
    trackViewCount: builder.mutation({
      query: ({ pestroutes_id, employee_name, location_id, employee_id }) => ({
        url: `/stats/portal/track-view-count`,
        method: "POST",
        body: {
          pestroutes_id,
          employee_name,
          location_id,
          employee_id,
        },
      }),
    }),
    getCompanySalesStatsNew: builder.query({
      query: ({ startDate, endDate }) => ({
        url: `/stats/portal/get-company-sales-stats-new`,
        method: "POST",
        body: {
          startDate: startDate
            ? new Date(startDate).toISOString().split("T")[0]
            : undefined,
          endDate: endDate
            ? new Date(endDate).toISOString().split("T")[0]
            : undefined,
        },
      }),
      providesTags: ["StatsApi"],
    }),
    // Network tab (Friend stats per rep). One fetch per date range; the page
    // filters and sorts the returned array in the browser.
    getNetworkSalesStats: builder.query({
      query: ({ startDate, endDate }) => ({
        url: `/stats/portal/get-network-sales-stats`,
        method: "POST",
        body: {
          startDate: startDate
            ? new Date(startDate).toISOString().split("T")[0]
            : undefined,
          endDate: endDate
            ? new Date(endDate).toISOString().split("T")[0]
            : undefined,
        },
      }),
      providesTags: ["StatsApi"],
    }),
    getCompanyStatsToday: builder.query({
      query: ({ officeId = 0 } = {}) => ({
        url: `/stats/portal/get-company-stats-today`,
        method: "POST",
        body: { officeId },
      }),
      providesTags: ["StatsApi"],
    }),
    getServiceStats: builder.query({
      query: ({ startDate, endDate }) => ({
        url: `/stats/portal/get-service-stats`,
        method: "POST",
        body: {
          startDate: startDate
            ? new Date(startDate).toISOString().split("T")[0]
            : undefined,
          endDate: endDate
            ? new Date(endDate).toISOString().split("T")[0]
            : undefined,
        },
      }),
      providesTags: ["StatsApi"],
    }),
    getAllEmployeesStats: builder.query({
      query: ({ startDate, endDate, page = 1, limit = 50, search = "", positions, location }) => ({
        url: `/stats/portal/get-all-employees-stats`,
        method: "POST",
        body: {
          startDate: startDate
            ? new Date(startDate).toISOString().split("T")[0]
            : undefined,
          endDate: endDate
            ? new Date(endDate).toISOString().split("T")[0]
            : undefined,
          page,
          limit,
          search: search || undefined,
          // Position filter: Airtable position RECORD IDs (e.g. ["recZDNS1K41SE2TeR"])
          positions: Array.isArray(positions) && positions.length > 0 ? positions : [],
          // Location filter: Airtable location RECORD ID (e.g. "rec1o1cyM8HB03zdD")
          location: location != null && location !== "" ? location : undefined,
        },
      }),
      providesTags: ["StatsApi"],
    }),
    getEmployeeStatsFilterOptions: builder.query({
      query: () => ({
        url: `/stats/portal/get-employee-stats-filter-options`,
        method: "GET",
      }),
      providesTags: ["StatsApi"],
    }),
    getAllViewCounts: builder.query({
      query: () => ({
        url: `/stats/portal/get-all-view-counts`,
        method: "GET",
      }),
      providesTags: ["StatsApi"],
    }),
    getStatsButtons: builder.query({
      query: () => ({
        url: `/stats/portal/get-training-buttons`,
        method: "GET",
      }),
      providesTags: ["StatsApi"],
    }),
    getWeeklyReport: builder.query({
      query: ({ startDate, endDate }) => ({
        url: `/stats/portal/get-weekly-report`,
        method: "POST",
        body: {
          startDate: startDate
            ? new Date(startDate).toISOString().split("T")[0]
            : undefined,
          endDate: endDate
            ? new Date(endDate).toISOString().split("T")[0]
            : undefined,
        },
      }),
      providesTags: ["StatsApi"],
    }),
    getWeeklyServiceSummary: builder.query({
      query: () => ({
        url: `/stats/portal/get-weekly-service-summary`,
        method: "GET",
      }),
      providesTags: ["StatsApi"],
    }),
    uploadRepProfilePic: builder.mutation({
      query: ({ pestroutesId, file }) => {
        const formData = new FormData();
        formData.append("image", file);
        formData.append("pestroutesId", String(pestroutesId));
        return {
          url: `/stats/portal/upload-rep-profile-pic`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["StatsApi"],
    }),
  }),
});

export const {
  useGetCompanySalesQuery,
  useGetCustomersQuery,
  useGetTeamsStatsQuery,
  useGetTeamLeaderStatsQuery,
  useGetBoardOfGloryQuery,
  useGetPersonalBestQuery,
  useGetSalesRepQuery,
  useGetTeamLeaderStatsSummaryQuery,
  useGetTeamLeaderStatsDetailsQuery,
  useGetCompanySalesStatsNewQuery,
  useGetCompanyStatsTodayQuery,
  useGetNetworkSalesStatsQuery,
  useGetServiceStatsQuery,
  useTrackViewCountMutation,
  useGetAllEmployeesStatsQuery,
  useGetEmployeeStatsFilterOptionsQuery,
  useGetAllViewCountsQuery,
  useGetStatsButtonsQuery,
  useGetWeeklyReportQuery,
  useGetWeeklyServiceSummaryQuery,
  useUploadRepProfilePicMutation,
} = statsApi;
